import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { McpSnippet } from '../types';

/* A snippet with its own Copy button. Snippets come only from generators
 * (placeholders, never a stored credential). */
export const CopyField = ({
  snippet,
  onCopied,
}: {
  snippet: McpSnippet;
  onCopied: (label: string) => void;
}) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet.text);
      setCopied(true);
      onCopied(snippet.label);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'mcp copy field', snippet: snippet.id },
      });
      onCopied('');
    }
  };
  const multiline = snippet.text.includes('\n');
  return (
    <div className='flex flex-col gap-1' data-testid={`snippet-${snippet.id}`}>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-xs font-medium text-muted-foreground'>
          {snippet.label}
        </span>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={() => void copy()}
          aria-label={`Copy ${snippet.label}`}
          data-testid={`copy-${snippet.id}`}
        >
          {copied ? (
            <Check className='size-3.5' />
          ) : (
            <Copy className='size-3.5' />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {multiline ? (
        <pre
          className={cn(
            'overflow-x-auto rounded-md bg-muted p-3 text-xs',
            snippet.language === 'text' && 'whitespace-pre-wrap break-words',
          )}
        >
          <code>{snippet.text}</code>
        </pre>
      ) : (
        <code className='rounded-md bg-muted px-2 py-1.5 text-xs break-all'>
          {snippet.text}
        </code>
      )}
    </div>
  );
};
